/**
 * DefaultColorsPanel - Configure default LED idle and pressed colors
 * Spec §5.11: Default LED behavior (USER-EDITABLE)
 */

import { useState } from 'react';
import { useConfigStore } from '@/store/configStore';
import { calculateMaxPressedBrightness } from '@/lib/powerBudget';
import { rgbToHex, hexToRgb } from '@/lib/colorUtils';

export function DefaultColorsPanel() {
  const ledDefaultBehavior = useConfigStore((state) => state.ledDefaultBehavior);
  const ledEnabled = useConfigStore((state) => state.ledSettings.enabled);
  const setIdleColor = useConfigStore((state) => state.setLEDIdleColor);
  const setIdleBrightness = useConfigStore((state) => state.setLEDIdleBrightness);
  const setPressedColor = useConfigStore((state) => state.setLEDPressedColor);
  const setPressedBrightness = useConfigStore((state) => state.setLEDPressedBrightness);
  const refreshColors = useConfigStore((state) => state.refreshColors);
  const columns = useConfigStore((s) => s.columns);
  const rows = useConfigStore((s) => s.rows);
  const keysPerModule = useConfigStore((s) => s.keysPerModule);
  const globalBrightness = useConfigStore((s) => s.ledSettings.brightness);
  const powerBudget = useConfigStore((s) => s.powerBudget);

  const maxSafeBrightness = calculateMaxPressedBrightness({
    columns, rows, keysPerModule,
    globalBrightness,
    idleColor: ledDefaultBehavior.idle.color,
    idleBrightness: ledDefaultBehavior.idle.brightness,
    pressedColor: ledDefaultBehavior.pressed.color,
    pressedBrightness: ledDefaultBehavior.pressed.brightness,
    maxSimultaneousPressed: powerBudget.maxSimultaneousPressed,
    converterAmps: powerBudget.converterAmps,
  });
  const isClamped = maxSafeBrightness < ledDefaultBehavior.pressed.brightness;

  const [refreshing, setRefreshing] = useState(false);

  const idleHex = rgbToHex(ledDefaultBehavior.idle.color);
  const pressedHex = rgbToHex(ledDefaultBehavior.pressed.color);

  const handleRefresh = () => {
    setRefreshing(true);
    refreshColors();
    // Visual feedback for 300ms
    setTimeout(() => setRefreshing(false), 300);
  };

  return (
    <>
      {/* Idle State - Spec §5.11 */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
          Idle State
        </h4>

        {/* Idle Color */}
        <div style={{ marginBottom: '8px' }}>
          <label
            htmlFor="idle-color"
            style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
          >
            Color:
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              id="idle-color"
              type="color"
              value={idleHex}
              onChange={(e) => setIdleColor(hexToRgb(e.target.value))}
              disabled={!ledEnabled}
              style={{
                width: '60px',
                height: '36px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                cursor: ledEnabled ? 'pointer' : 'not-allowed',
              }}
            />
            <span style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
              RGB({ledDefaultBehavior.idle.color[0]}, {ledDefaultBehavior.idle.color[1]}, {ledDefaultBehavior.idle.color[2]})
            </span>
          </div>
        </div>

        {/* Idle Brightness */}
        <div style={{ marginBottom: '8px' }}>
          <label
            htmlFor="idle-brightness"
            style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
          >
            Local Brightness: {ledDefaultBehavior.idle.brightness.toFixed(2)}
          </label>
          <input
            id="idle-brightness"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={ledDefaultBehavior.idle.brightness}
            onChange={(e) => setIdleBrightness(parseFloat(e.target.value))}
            disabled={!ledEnabled}
            style={{
              width: '100%',
              cursor: ledEnabled ? 'pointer' : 'not-allowed',
            }}
          />
          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
            Multiplies with global brightness (0.0-1.0)
          </div>
        </div>
      </div>

      {/* Pressed State - Spec §5.11 */}
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
          Pressed State
        </h4>

        {/* Pressed Color */}
        <div style={{ marginBottom: '8px' }}>
          <label
            htmlFor="pressed-color"
            style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
          >
            Color:
          </label>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              id="pressed-color"
              type="color"
              value={pressedHex}
              onChange={(e) => setPressedColor(hexToRgb(e.target.value))}
              disabled={!ledEnabled}
              style={{
                width: '60px',
                height: '36px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                cursor: ledEnabled ? 'pointer' : 'not-allowed',
              }}
            />
            <span style={{ fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>
              RGB({ledDefaultBehavior.pressed.color[0]}, {ledDefaultBehavior.pressed.color[1]}, {ledDefaultBehavior.pressed.color[2]})
            </span>
          </div>
        </div>

        {/* Pressed Brightness */}
        <div style={{ marginBottom: '8px' }}>
          <label
            htmlFor="pressed-brightness"
            style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
          >
            Local Brightness: {ledDefaultBehavior.pressed.brightness.toFixed(2)}
          </label>
          <input
            id="pressed-brightness"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={ledDefaultBehavior.pressed.brightness}
            onChange={(e) => setPressedBrightness(parseFloat(e.target.value))}
            disabled={!ledEnabled}
            style={{
              width: '100%',
              cursor: ledEnabled ? 'pointer' : 'not-allowed',
            }}
          />
          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
            Multiplies with global brightness (0.0-1.0)
          </div>
          {isClamped && (
            <div style={{
              marginTop: '4px',
              fontSize: '12px',
              color: '#856404',
              backgroundColor: '#fff3cd',
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #ffc107',
            }}>
              Effective: {maxSafeBrightness.toFixed(2)} (limited by power budget)
            </div>
          )}
        </div>
      </div>

      {/* Refresh Button */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={handleRefresh}
          disabled={!ledEnabled}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: refreshing ? '#28a745' : '#007bff',
            border: 'none',
            borderRadius: '4px',
            cursor: ledEnabled ? 'pointer' : 'not-allowed',
            transition: 'background-color 0.2s',
            opacity: ledEnabled ? 1 : 0.5,
          }}
          onMouseEnter={(e) => {
            if (ledEnabled && !refreshing) {
              e.currentTarget.style.backgroundColor = '#0056b3';
            }
          }}
          onMouseLeave={(e) => {
            if (ledEnabled && !refreshing) {
              e.currentTarget.style.backgroundColor = '#007bff';
            }
          }}
        >
          {refreshing ? '✓ Colors Refreshed!' : 'Refresh Colors'}
        </button>
      </div>

      {/* Informational note - Spec §5.11 */}
      <div
        style={{
          padding: '8px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#6c757d',
        }}
      >
        Note: Pressed state is for future interactive features. Currently showing idle state.
      </div>
    </>
  );
}
